import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Calendar, Clock, MapPin, Users, Check, X, Loader2,
  UserCheck, UserX, AlertCircle, Plus
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config/api';

export default function AttendanceTracker({ teamId, user }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newSession, setNewSession] = useState({
    team_id: teamId || '',
    title: '',
    session_type: 'training',
    date: new Date().toISOString().split('T')[0],
    start_time: '10:00',
    end_time: '12:00',
    location: ''
  });

  useEffect(() => {
    if (teamId) {
      fetchSessions();
    }
  }, [teamId]);

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/attendance/sessions?team_id=${teamId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessions(response.data.sessions || []);
    } catch (err) {
      console.error('Failed to fetch sessions:', err);
    } finally {
      setLoading(false);
    }
  };

  const createSession = async () => {
    if (!newSession.title || !newSession.date) {
      toast.error('Title and date are required');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/attendance/sessions`, {
        ...newSession,
        team_id: teamId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Session created');
      setShowCreateModal(false);
      fetchSessions();
    } catch (err) {
      toast.error('Failed to create session');
    }
  };

  const getSessionDetails = async (sessionId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/attendance/sessions/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedSession(response.data);
    } catch (err) {
      console.error('Failed to fetch session details:', err);
    }
  };

  const markAttendance = async (childId, status) => {
    if (!selectedSession) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/attendance/sessions/${selectedSession.id}/check-in`, {
        child_id: childId,
        status: status
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Attendance recorded');
      getSessionDetails(selectedSession.id);
    } catch (err) {
      toast.error('Failed to record attendance');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'present': return 'bg-green-500/20 text-green-400';
      case 'late': return 'bg-amber-500/20 text-amber-400';
      case 'absent': return 'bg-red-500/20 text-red-400';
      case 'excused': return 'bg-blue-500/20 text-blue-400';
      default: return 'bg-zinc-700 text-zinc-400';
    }
  };

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6" data-testid="attendance-tracker">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-xl flex items-center justify-center">
            <UserCheck className="h-5 w-5 text-green-400" />
          </div>
          <div>
            <h3 className="font-semibold text-white">Attendance Tracker</h3>
            <p className="text-xs text-zinc-500">Track training attendance</p>
          </div>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          className="bg-gradient-to-r from-green-500 to-emerald-500 text-white text-sm"
          data-testid="create-session-btn"
        >
          <Plus className="h-4 w-4 mr-1" />
          New Session
        </Button>
      </div>

      {/* Sessions List */}
      <div className="space-y-2 max-h-[350px] overflow-y-auto">
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-green-400" />
          </div>
        ) : sessions.length === 0 ? (
          <div className="text-center py-8 text-zinc-500">
            <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No sessions scheduled</p>
          </div>
        ) : (
          sessions.map(session => (
            <div
              key={session.id}
              onClick={() => getSessionDetails(session.id)}
              className="p-4 bg-zinc-800/50 rounded-xl cursor-pointer hover:bg-zinc-800 transition-all border border-zinc-700/50"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-white">{session.title}</p>
                  <div className="flex items-center gap-3 mt-1 text-xs text-zinc-500">
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {session.date}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {session.start_time}
                    </span>
                    {session.location && (
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {session.location}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 rounded text-xs ${
                    session.session_type === 'match' ? 'bg-red-500/20 text-red-400' :
                    session.session_type === 'assessment' ? 'bg-purple-500/20 text-purple-400' :
                    'bg-green-500/20 text-green-400'
                  }`}>
                    {session.session_type}
                  </span>
                  <span className="text-xs text-zinc-400">
                    <Users className="h-3 w-3 inline mr-1" />
                    {session.attendance_count || 0}
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Session Details Modal */}
      {selectedSession && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setSelectedSession(null)}>
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-2xl p-6 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-bold text-white">{selectedSession.title}</h3>
                <p className="text-sm text-zinc-500">{selectedSession.date} • {selectedSession.start_time}</p>
              </div>
              <button onClick={() => setSelectedSession(null)} className="text-zinc-500 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-3">
              <h4 className="text-sm font-medium text-zinc-400">Roster</h4>
              {selectedSession.roster?.map(athlete => {
                const record = selectedSession.attendance_records?.find(r => r.child_id === athlete.id);
                return (
                  <div key={athlete.id} className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                    <span className="text-white">{athlete.name}</span>
                    <div className="flex items-center gap-2">
                      {record ? (
                        <span className={`px-3 py-1 rounded-full text-xs ${getStatusColor(record.status)}`}>
                          {record.status}
                        </span>
                      ) : (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => markAttendance(athlete.id, 'present')}
                            className="text-green-400 hover:bg-green-500/20"
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => markAttendance(athlete.id, 'absent')}
                            className="text-red-400 hover:bg-red-500/20"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => markAttendance(athlete.id, 'late')}
                            className="text-amber-400 hover:bg-amber-500/20"
                          >
                            <Clock className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Create Session Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowCreateModal(false)}>
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-white">Create Session</h3>
              <button onClick={() => setShowCreateModal(false)} className="text-zinc-500 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <Input
                placeholder="Session Title"
                value={newSession.title}
                onChange={e => setNewSession({...newSession, title: e.target.value})}
                className="bg-zinc-800 border-zinc-700 text-white"
              />

              <select
                value={newSession.session_type}
                onChange={e => setNewSession({...newSession, session_type: e.target.value})}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg p-3 text-white"
              >
                <option value="training">Training</option>
                <option value="match">Match</option>
                <option value="meeting">Meeting</option>
                <option value="assessment">Assessment</option>
              </select>

              <Input
                type="date"
                value={newSession.date}
                onChange={e => setNewSession({...newSession, date: e.target.value})}
                className="bg-zinc-800 border-zinc-700 text-white"
              />

              <div className="grid grid-cols-2 gap-4">
                <Input
                  type="time"
                  value={newSession.start_time}
                  onChange={e => setNewSession({...newSession, start_time: e.target.value})}
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
                <Input
                  type="time"
                  value={newSession.end_time}
                  onChange={e => setNewSession({...newSession, end_time: e.target.value})}
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>

              <Input
                placeholder="Location"
                value={newSession.location}
                onChange={e => setNewSession({...newSession, location: e.target.value})}
                className="bg-zinc-800 border-zinc-700 text-white"
              />

              <Button
                onClick={createSession}
                className="w-full bg-gradient-to-r from-green-500 to-emerald-500 text-white"
              >
                Create Session
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
